import Layout from '@/components/feature/Layout';
import { companyInfo } from '@/mocks/company';
import { products } from '@/mocks/products';
import { Link } from 'react-router-dom';

const whatsappMessage = encodeURIComponent('Olá! Estou no site da Maquinasdesoldageomembrana.com.br e gostaria de informações sobre máquinas de solda de geomembrana.');
const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${whatsappMessage}`;

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Máquina de Solda de Geomembrana',
  description: 'Conheça o que é uma máquina de solda geomembrana, tipos, funcionamento, aplicações e modelos disponíveis.',
  url: 'https://maquinadesoldageomembrana.com.br/maquina-de-solda-de-geomembrana',
  inLanguage: 'pt-BR',
};

export default function MaquinaDeSoldaPage() {
  const cunhaQuente = products.filter((p) => p.category === 'Cunha Quente');
  const extrusoras = products.filter((p) => p.category === 'Extrusora');

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <nav className="text-xs text-foreground-400 mb-6">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line mx-1" />
          <span className="text-foreground-600">Máquina de Solda de Geomembrana</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-6">
              Máquina de Solda de Geomembrana
            </h1>

            <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
              <p>
                Uma <strong className="text-foreground-950">máquina de solda de geomembrana</strong> é um equipamento industrial utilizado para unir folhas de geomembrana impermeável em grandes áreas contínuas. A soldagem garante a estanqueidade do sistema, essencial para projetos de impermeabilização profissional em aterros sanitários, reservatórios, barragens, tanques de piscicultura e mineração.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Como Funciona a Soldagem</h2>
              <p>
                O processo de soldagem de geomembrana utiliza calor controlado para fundir as bordas de duas folhas sobrepostas, criando uma junta hermética. Existem dois métodos principais:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-5">
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-2 flex items-center gap-2">
                    <i className="ri-fire-line text-primary-500" />
                    Cunha Quente
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    Uma cunha metálica aquecida funde simultaneamente as duas folhas, criando uma <strong>solda dupla com canal de teste de pressão</strong>. É o método mais rápido para grandes áreas planas e atende à norma NBR 16199.
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-2 flex items-center gap-2">
                    <i className="ri-drop-line text-accent-500" />
                    Extrusão
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    Aplica fio de PEAD fundido sobre a emenda por rosca extrusora. Gera <strong>alta resistência mecânica</strong> e é indicada para cantos, junções, reparos e geomembranas espessas acima de 2 mm.
                  </p>
                </div>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Tipos de Máquinas Disponíveis</h2>

              <h3 className="text-base font-bold text-foreground-900 uppercase mt-6 mb-2">Cunha Quente Automática</h3>
              <p>
                Equipamentos com tração motorizada que avançam automaticamente sobre a geomembrana, mantendo velocidade e temperatura constantes. Indicadas para <strong>grandes áreas planas</strong> como aterros sanitários, barragens e reservatórios. Modelos disponíveis: <strong>BT950, CX500L, BT-GM1, Weldy WGW300 Plus</strong>.
              </p>
              <p>
                <strong className="text-foreground-950">Observação:</strong> a máquina de <strong>ar quente de alta pressão</strong> <strong>LC3000C</strong> não é uma cunha quente — é exclusiva para soldagem de <strong>lona de PVC</strong> (banners, toldos e coberturas) e não é indicada para geomembrana.
              </p>

              <h3 className="text-base font-bold text-foreground-900 uppercase mt-6 mb-2">Cunha Quente Manual</h3>
              <p>
                Operadas manualmente pelo operador, oferecem controle direto da velocidade e pressão. São mais leves, econômicas e ideais para <strong>trabalhos em locais de acesso restrito</strong> e pequenas áreas. Modelos: <strong>BT800, BT1600, CX300 1400W</strong>.
              </p>

              <h3 className="text-base font-bold text-foreground-900 uppercase mt-6 mb-2">Extrusoras Manuais</h3>
              <p>
                Indispensáveis em todo projeto de impermeabilização como <strong>equipamento complementar</strong>. Realizam soldagem por extrusão de fio de PEAD para emendas, cantos e reparos. Modelos: <strong>Extrusora EX3, Weldy Booster EX3 Plus, Extrusora Metabo BT610A</strong>.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Materiais Compatíveis</h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>PEAD (Polietileno de Alta Densidade)</strong> — mais utilizado em aterros, barragens e mineração. Espessuras de 0,5 a 3,0 mm.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>PELBD / LLDPE</strong> — polietileno linear de baixa densidade, usado em piscicultura e reservatórios.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>PVC (Policloreto de Vinila)</strong> — impermeabilização de telhados e piscinas.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>PP (Polipropileno)</strong> — biodigestores e lagoas de tratamento.</span></li>
              </ul>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Como Escolher a Máquina Ideal</h2>
              <p>A escolha depende de fatores técnicos específicos do projeto:</p>
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 my-4">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2"><span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold flex-shrink-0">1</span><span><strong>Espessura da geomembrana</strong> — máquinas com maior potência para espessuras superiores a 2,0 mm.</span></li>
                  <li className="flex items-start gap-2"><span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold flex-shrink-0">2</span><span><strong>Área de cobertura</strong> — cunha quente automática para grandes áreas planas; manual para áreas menores.</span></li>
                  <li className="flex items-start gap-2"><span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold flex-shrink-0">3</span><span><strong>Tipo de material</strong> — verificar compatibilidade PEAD/PVC/LLDPE na especificação técnica.</span></li>
                  <li className="flex items-start gap-2"><span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold flex-shrink-0">4</span><span><strong>Condições de campo</strong> — terreno plano vs. declive; acesso do operador; clima.</span></li>
                </ul>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Manutenção e Segurança</h2>
              <p>
                A manutenção preventiva garante a durabilidade do equipamento e a qualidade das soldas. Recomenda-se: limpeza regular da cunha, verificação do sistema de aquecimento, lubrificação das partes móveis e troca de peças de desgaste conforme o manual do fabricante. Sempre realize um <strong>solda de teste (test weld)</strong> no início de cada jornada de trabalho.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              <Link to="/modelos-de-maquinas" className="text-sm text-primary-600 hover:text-primary-700 font-medium bg-primary-50 px-3 py-1.5 rounded-full cursor-pointer">Ver Comparativo de Modelos</Link>
              <Link to="/como-soldar-geomembrana" className="text-sm text-primary-600 hover:text-primary-700 font-medium bg-primary-50 px-3 py-1.5 rounded-full cursor-pointer">Guia Passo a Passo</Link>
              <Link to="/soldagem-de-geomembrana" className="text-sm text-primary-600 hover:text-primary-700 font-medium bg-primary-50 px-3 py-1.5 rounded-full cursor-pointer">Técnicas de Soldagem</Link>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-primary-50 rounded-2xl border border-primary-200/70 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950 uppercase mb-3">Solicitar Orçamento</h3>
              <p className="text-foreground-500 text-sm leading-relaxed mb-5">Indicamos o equipamento ideal para seu projeto em minutos.</p>
              <a href={whatsappUrl} target="_blank" rel="nofollow noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide">
                <i className="ri-whatsapp-line" /> Falar no WhatsApp
              </a>
            </div>

            <div className="bg-white rounded-2xl border border-background-200/70 p-5">
              <h4 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-3">Cunha Quente ({cunhaQuente.length} modelos)</h4>
              <div className="space-y-2">
                {cunhaQuente.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm py-2 border-b border-background-200/40 last:border-0">
                    <span className="text-foreground-800 font-medium">{p.name}</span>
                    <span className="text-foreground-400 text-xs">{p.specs.potencia}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-background-200/70 p-5">
              <h4 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-3">Extrusoras ({extrusoras.length} modelos)</h4>
              <div className="space-y-2">
                {extrusoras.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm py-2 border-b border-background-200/40 last:border-0">
                    <span className="text-foreground-800 font-medium">{p.name}</span>
                    <span className="text-foreground-400 text-xs">{p.specs.potencia}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}